'use client';

import { useCallback, useState } from 'react';

import type { DocumentReorderItem, EditableDocument } from '../types';

interface UseDocumentReorderOptions<T extends EditableDocument> {
  documents: T[];
  onReorder: (items: DocumentReorderItem[]) => Promise<void>;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseDocumentReorderReturn<T extends EditableDocument> {
  sortedDocuments: T[];
  isReordering: boolean;
  moveUp: (index: number) => Promise<void>;
  moveDown: (index: number) => Promise<void>;
  canMoveUp: (index: number) => boolean;
  canMoveDown: (index: number) => boolean;
}

/**
 * Hook for managing document reordering
 *
 * @example
 * ```tsx
 * const {
 *   sortedDocuments,
 *   isReordering,
 *   moveUp,
 *   moveDown,
 *   canMoveUp,
 *   canMoveDown,
 * } = useDocumentReorder({
 *   documents,
 *   onReorder: async (items) => {
 *     await reorderDocumentsAction({ items });
 *   },
 * });
 * ```
 */
export function useDocumentReorder<T extends EditableDocument>({
  documents,
  onReorder,
  onSuccess,
  onError,
}: UseDocumentReorderOptions<T>): UseDocumentReorderReturn<T> {
  const [isReordering, setIsReordering] = useState(false);

  // Sort documents by displayOrder
  const sortedDocuments = [...documents].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

  const canMoveUp = useCallback(
    (index: number) => index > 0 && !isReordering,
    [isReordering],
  );

  const canMoveDown = useCallback(
    (index: number) => index < sortedDocuments.length - 1 && !isReordering,
    [isReordering, sortedDocuments.length],
  );

  const moveUp = useCallback(
    async (index: number) => {
      if (!canMoveUp(index)) return;

      setIsReordering(true);
      try {
        const currentDoc = sortedDocuments[index];
        const previousDoc = sortedDocuments[index - 1];

        if (!currentDoc || !previousDoc) return;

        const items: DocumentReorderItem[] = [
          {
            id: currentDoc.id,
            displayOrder: previousDoc.displayOrder ?? index - 1,
          },
          {
            id: previousDoc.id,
            displayOrder: currentDoc.displayOrder ?? index,
          },
        ];

        await onReorder(items);
        onSuccess?.();
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Reorder failed'));
      } finally {
        setIsReordering(false);
      }
    },
    [canMoveUp, sortedDocuments, onReorder, onSuccess, onError],
  );

  const moveDown = useCallback(
    async (index: number) => {
      if (!canMoveDown(index)) return;

      setIsReordering(true);
      try {
        const currentDoc = sortedDocuments[index];
        const nextDoc = sortedDocuments[index + 1];

        if (!currentDoc || !nextDoc) return;

        const items: DocumentReorderItem[] = [
          {
            id: currentDoc.id,
            displayOrder: nextDoc.displayOrder ?? index + 1,
          },
          { id: nextDoc.id, displayOrder: currentDoc.displayOrder ?? index },
        ];

        await onReorder(items);
        onSuccess?.();
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Reorder failed'));
      } finally {
        setIsReordering(false);
      }
    },
    [canMoveDown, sortedDocuments, onReorder, onSuccess, onError],
  );

  return {
    sortedDocuments,
    isReordering,
    moveUp,
    moveDown,
    canMoveUp,
    canMoveDown,
  };
}
